/**
 * AnalysisHistoryCard — List of client analysis history rows.
 *
 * Uses inline history rows (adapted from AnalysisFeedRow pattern) with DeltaBadge.
 * Note: AnalysisFeedRow from dashboard shows clientName/avatars; history rows show
 * date/pattern/score/delta — different enough to warrant dedicated row rendering here.
 *
 * Privacy notice shown at bottom when canViewOriginalMessage = false.
 * Consent gate shown in place of rows when canViewHistory = false.
 *
 * Row subtitle text uses #6B7280 (corrected from #A1A1AA — WCAG AA 4.7:1).
 */

import React, { useCallback } from 'react';
import { View, Text, Pressable, StyleSheet, ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { TFunction } from '@dramadetector/shared';
import { GlassCard } from '../../glass/GlassCard';
import { DeltaBadge } from './DeltaBadge';
import { PrivacyNotice } from './PrivacyNotice';
import { ConsentGate } from './ConsentGate';

interface AnalysisHistoryItem {
  id: string;
  date: string;
  patternLabel: string;
  score: number;
  delta: number | null;
  isNew: boolean;
  onPress: (id: string) => void;
}

interface AnalysisHistoryCardProps {
  items: AnalysisHistoryItem[];
  totalCount: number;
  canViewHistory: boolean;
  canViewOriginalMessage: boolean;
  clientName: string;
  t: TFunction<'practitioner'>;
}

interface HistoryRowProps {
  item: AnalysisHistoryItem;
  t: TFunction<'practitioner'>;
}

const HistoryRow: React.FC<HistoryRowProps> = React.memo(function HistoryRow({ item, t }) {
  const handlePress = useCallback(() => {
    item.onPress(item.id);
  }, [item.id, item.onPress]);

  const a11yLabel = `${item.date}: ${item.patternLabel}, ${t('history.score' as never)}: ${item.score.toFixed(1)}${item.delta != null ? `, ${item.delta < 0 ? t('trend.improving' as never) : item.delta > 0 ? t('trend.worsening' as never) : ''}` : ''}`;

  return (
    <Pressable
      style={({ pressed }) => [styles.row, pressed && styles.rowPressed]}
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel={a11yLabel}
    >
      <View style={styles.fileIconWrapper}>
        <Ionicons name="document-text-outline" size={16} color="#EC4899" />
      </View>

      <View style={styles.textGroup}>
        <View style={styles.topLine}>
          <Text style={styles.dateText} numberOfLines={1}>{item.date}</Text>
          {item.delta != null ? (
            <DeltaBadge delta={item.delta} isNew={item.isNew} t={t} />
          ) : (
            <View
              style={styles.scorePill}
              accessibilityElementsHidden={true}
              importantForAccessibility="no-hide-descendants"
            >
              <Text style={styles.scoreText}>{item.score.toFixed(1)}</Text>
            </View>
          )}
        </View>
        <Text style={styles.subText} numberOfLines={1}>
          {item.patternLabel}
        </Text>
      </View>

      <Ionicons name="chevron-forward" size={16} color="#D4D4D8" />
    </Pressable>
  );
});

export const AnalysisHistoryCard: React.FC<AnalysisHistoryCardProps> = ({
  items,
  totalCount,
  canViewHistory,
  canViewOriginalMessage,
  clientName,
  t,
}) => (
  <GlassCard withChromeBorder={false} noPadding={true}>
    {/* Header */}
    <View style={styles.header}>
      <Text style={styles.headerTitle}>{t('history.title')}</Text>
      <Text style={styles.headerCount}>
        {totalCount} {t('history.total')}
      </Text>
    </View>

    {/* Body */}
    {canViewHistory ? (
      <>
        {items.map((item) => (
          <HistoryRow key={item.id} item={item} t={t} />
        ))}
        {!canViewOriginalMessage && <PrivacyNotice t={t} />}
      </>
    ) : (
      <ConsentGate type="history" clientName={clientName} t={t} />
    )}
  </GlassCard>
);

const styles = StyleSheet.create({
  header: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.4)',
  } as ViewStyle,
  headerTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#374151',
  },
  headerCount: {
    fontSize: 12,
    color: '#6B7280',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 13,
    paddingHorizontal: 16,
    minHeight: 60,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.4)',
  } as ViewStyle,
  rowPressed: {
    backgroundColor: 'rgba(167, 139, 250, 0.06)',
  },
  fileIconWrapper: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: 'rgba(236, 72, 153, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  textGroup: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  topLine: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  } as ViewStyle,
  dateText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#18181B',
    flexShrink: 1,
  },
  subText: {
    fontSize: 12,
    color: '#6B7280', // Corrected from #A1A1AA for WCAG AA 4.7:1
    marginTop: 1,
  },
  scorePill: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 9999,
    backgroundColor: '#FCE7F3',
  },
  scoreText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#BE123C',
  },
});
